import { ErrorResponse, ErrorResponseObject, FieldErrorMessage } from "./types";

// What the API answers with when a failure carries no explanation of its own (a 500, a network
// drop). Exported so a caller can recognise that case instead of string-matching.
export const SERVER_ERROR_MESSAGE = "Server Error";

export const isErrorResponseObject = (
  error: any,
): error is ErrorResponseObject => {
  return (
    typeof error === "object" &&
    "constraints" in error &&
    typeof error.constraints === "object"
  );
};

// Returns an empty array — not a fallback message — when the response explains nothing, so a
// caller can tell "the server said nothing" apart from "the server said something".
export const getFieldErrorMessages = (
  errorResponse: ErrorResponse | string,
): FieldErrorMessage[] => {
  // express-rate-limit answers a 429 with a plain string body rather than the API's
  // `{ errors: [...] }` envelope, and that text is the only thing telling the user to wait.
  if (typeof errorResponse === "string") {
    return errorResponse.trim() ? [{ message: errorResponse }] : [];
  }

  if (!errorResponse?.errors || !Array.isArray(errorResponse.errors)) {
    return [];
  }

  const fieldErrors: FieldErrorMessage[] = [];
  errorResponse.errors.forEach((error) => {
    if (typeof error === "string") {
      fieldErrors.push({ message: error });
    }
    if (isErrorResponseObject(error)) {
      Object.values(error.constraints).forEach((message) => {
        fieldErrors.push({ property: error.property, message });
      });
    }
  });

  return fieldErrors;
};

// Takes `unknown` rather than `ErrorResponse` since callers hand it whatever the failure
// produced (a rejected RTK Query payload, a caught `err`); a network drop answers `false` instead of throwing.
export const hasFieldError = (
  errorResponse: unknown,
  property: string,
): boolean =>
  getFieldErrorMessages(errorResponse as ErrorResponse | string).some(
    (fieldError) => fieldError.property === property,
  );

// A 400 naming `cursor` means a token held across a deploy was refused, not a user error — so
// `useAccountActivity` recovers silently (reloads page one) instead of toasting. One shared predicate keeps that pair from drifting.
export const isTradeCursorRejection = (error: unknown): boolean =>
  hasFieldError(error, "cursor");

// A 409 whose recovery is switching the feed to cursor paging, not retrying the same page.
export const isNumberedActivityUnavailable = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  (error as ErrorResponse).code === "NUMBERED_ACTIVITY_PAGE_UNAVAILABLE";

export const getErrorMessages = (
  errorResponse: ErrorResponse | string,
): string[] => {
  const errorMessages = getFieldErrorMessages(errorResponse).map(
    ({ message }) => message,
  );

  return errorMessages.length ? errorMessages : [SERVER_ERROR_MESSAGE];
};
